<template>
  <DefaultLayout background-image="green">
    <div
      class="share-instructions-page grid h-full w-full grid-rows-[auto,minmax(0,1fr),auto] justify-items-center overflow-hidden px-5 py-5 text-jm_white"
    >
      <div class="share-instructions-header flex shrink-0 flex-col items-center">
        <img
          :src="logo"
          alt="logo"
          class="share-instructions-logo mb-4 w-[92px]"
        />
        <div
          class="share-instructions-title mb-2 text-center font-bold uppercase leading-none"
        >
          {{ $t("share_instructions.title") }}
        </div>
        <div
          v-if="$t('share_instructions.description')"
          class="share-instructions-description max-w-[18rem] text-center leading-6"
        >
          {{ $t("share_instructions.description") }}
        </div>
      </div>

      <div
        class="share-instructions-stage flex min-h-0 w-full max-w-[22rem] flex-col items-center justify-center py-3"
      >
        <div
          class="share-instructions-preview relative flex items-center justify-center overflow-hidden rounded-[1.5rem] border-2 border-jm_white/40 bg-black/30 shadow-2xl"
        >
          <img
            v-if="capturedPhoto"
            :src="capturedPhoto"
            alt="Captured selfie"
            class="h-full w-full object-cover"
          />
          <div
            v-else
            class="flex h-full w-full items-center justify-center px-8 text-center text-sm uppercase tracking-[0.2em] text-jm_white/70"
          >
            {{ $t("photo_capture.placeholder") }}
          </div>
        </div>

        <div
          class="share-instructions-steps mt-4 max-w-[20rem] text-center text-sm leading-6 text-jm_white/80"
        >
          {{ $t("share_instructions.steps") }}
        </div>

        <div
          class="share-instructions-bartender mt-2 max-w-[20rem] text-center text-sm font-semibold leading-6 text-jm_white"
        >
          {{ $t("share_instructions.bartender_note") }}
        </div>

        <div
          v-if="shareError"
          class="mt-4 text-center text-sm leading-5 text-jm_orange"
        >
          {{ shareError }}
        </div>
      </div>

      <div
        class="share-instructions-actions flex w-full max-w-[22rem] shrink-0 flex-col gap-3 self-end"
      >
        <button
          class="share-instructions-button min-h-12 rounded-xl bg-jm_orange px-4 text-sm font-bold uppercase tracking-[0.15em] text-white"
          :disabled="isSharing"
          :class="{ 'cursor-not-allowed opacity-50': isSharing }"
          @click="handleInstagramShare"
        >
          {{
            isSharing
              ? $t("share_instructions.sharing")
              : $t("share_instructions.share_button")
          }}
        </button>

        <button
          class="share-instructions-button min-h-12 rounded-xl border-2 border-jm_white/70 bg-white/10 px-4 text-sm font-bold uppercase tracking-[0.15em] text-jm_white backdrop-blur-sm"
          :disabled="isSharing"
          :class="{ 'cursor-not-allowed opacity-50': isSharing }"
          @click="goBackToPhoto"
        >
          {{ $t("share_instructions.back_button") }}
        </button>
      </div>
    </div>
  </DefaultLayout>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import DefaultLayout from "../layouts/DefaultLayout.vue";
import logo from "@/assets/images/misc/logo_jaegermeister_white.svg";
import { useGlobalStore } from "@/stores/global";
import { useGameTracking } from "@/composables/useGameTracking";
import { uploadCapturedPhoto } from "@/services/pimImageUpload";

type SharePayload = {
  files?: File[];
  title?: string;
  text?: string;
};

const globalStore = useGlobalStore();
const { kpiTracker, session } = useGameTracking();

const capturedPhoto = computed(() => globalStore.capturedPhotoDataUrl);
const shareError = ref("");
const isSharing = ref(false);
const didLeaveForShare = ref(false);
const finishFallbackTimer = ref<number | null>(null);

const dataUrlToFile = (dataUrl: string) => {
  const [header, base64Content] = dataUrl.split(",");

  if (!header || !base64Content) {
    throw new Error("The photo data could not be prepared for sharing.");
  }

  const mimeMatch = header.match(/data:(.*?);base64/);
  const mimeType = mimeMatch?.[1] || "image/jpeg";
  const binary = window.atob(base64Content);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new File([bytes], `jaegermeister-shot-${Date.now()}.jpg`, {
    type: mimeType,
  });
};

const createShareFile = () => {
  if (globalStore.capturedPhotoFile) {
    return globalStore.capturedPhotoFile;
  }

  if (!capturedPhoto.value) {
    throw new Error("Missing image data for share");
  }

  return dataUrlToFile(capturedPhoto.value);
};

const shareImageWithSystemShareSheet = async (shareFile: File) => {
  const nav = navigator as Navigator & {
    canShare?: (data?: SharePayload) => boolean;
    share: (data?: SharePayload) => Promise<void>;
  };
  const shareData: SharePayload = {
    files: [shareFile],
    title: "Jagermeister Shot Machine",
  };

  if (!nav.share) {
    throw new Error(
      "Sharing this photo is not supported on this device. Please save it manually from the preview."
    );
  }

  if (nav.canShare && !nav.canShare(shareData)) {
    throw new Error(
      "This device cannot share the generated photo file. Please save it manually from the preview."
    );
  }

  await nav.share(shareData);
};

const ensurePhotoUploaded = async () => {
  if (!capturedPhoto.value) {
    throw new Error("Missing image data for PIM upload");
  }

  if (globalStore.uploadedPhotoItemId !== null) {
    return globalStore.uploadedPhotoItemId;
  }

  const itemId = await uploadCapturedPhoto(capturedPhoto.value);
  globalStore.setUploadedPhotoItemId(itemId);
  kpiTracker.setEnteredCrmData(true);

  try {
    await session.updateSessionProgress(kpiTracker.getKpiData(), {
      step: "photo_uploaded",
    });
  } catch (error) {
    console.error("[ShareInstructions] Error while tracking photo upload:", error);
  }

  return itemId;
};

const finishRaffleFlow = () => {
  clearFinishFallbackTimer();
  globalStore.changeStep("raffleMove");
};

const clearFinishFallbackTimer = () => {
  if (finishFallbackTimer.value === null) {
    return;
  }

  window.clearTimeout(finishFallbackTimer.value);
  finishFallbackTimer.value = null;
};

const scheduleRaffleFallback = () => {
  clearFinishFallbackTimer();
  finishFallbackTimer.value = window.setTimeout(() => {
    if (globalStore.currentStep === "shareInstructions") {
      finishRaffleFlow();
    }
  }, 1200);
};

const handleVisibilityChange = () => {
  if (document.visibilityState === "hidden") {
    didLeaveForShare.value = true;
    return;
  }

  if (document.visibilityState === "visible" && didLeaveForShare.value) {
    didLeaveForShare.value = false;
    finishRaffleFlow();
  }
};

const handlePageHide = () => {
  didLeaveForShare.value = true;
};

const handlePageShowOrFocus = () => {
  if (didLeaveForShare.value) {
    didLeaveForShare.value = false;
    finishRaffleFlow();
  }
};

const handleInstagramShare = async () => {
  if (isSharing.value) {
    return;
  }

  if (!capturedPhoto.value) {
    globalStore.changeStep("photoCapture");
    return;
  }

  isSharing.value = true;
  shareError.value = "";

  try {
    const shareFile = createShareFile();
    await ensurePhotoUploaded();
    await shareImageWithSystemShareSheet(shareFile);
    scheduleRaffleFallback();
  } catch (error) {
    console.error("[ShareInstructions] Error while sharing image:", error);
    shareError.value =
      error instanceof Error
        ? error.message
        : "The photo could not be shared.";
  } finally {
    isSharing.value = false;
  }
};

const goBackToPhoto = () => {
  if (isSharing.value) {
    return;
  }

  globalStore.playTapSound();
  globalStore.changeStep("photoCapture");
};

onMounted(() => {
  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("pagehide", handlePageHide);
  window.addEventListener("pageshow", handlePageShowOrFocus);
  window.addEventListener("focus", handlePageShowOrFocus);
});

onBeforeUnmount(() => {
  clearFinishFallbackTimer();
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  window.removeEventListener("pagehide", handlePageHide);
  window.removeEventListener("pageshow", handlePageShowOrFocus);
  window.removeEventListener("focus", handlePageShowOrFocus);
  if (globalStore.currentStep !== "raffleMove") {
    kpiTracker.resetTrackingData();
  }
});
</script>

<style lang="scss" scoped>
@import "@/assets/sass/main.sass";

.share-instructions-page {
  padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 0.75rem);
  row-gap: 0.75rem;
}

.share-instructions-logo {
  width: 92px;
}

.share-instructions-title {
  font-size: clamp(2rem, 5vw, 2.5rem);
}

.share-instructions-description {
  font-size: clamp(0.95rem, 2.7vw, 1.05rem);
}

.share-instructions-preview {
  height: 100%;
  width: min(50vw, 10.75rem);
  max-width: 100%;
  min-height: 0;
  aspect-ratio: 3 / 4;
}

.share-instructions-button {
  min-height: 3rem;
}

.share-instructions-stage {
  width: 100%;
}

.share-instructions-actions {
  padding-top: 0.5rem;
}

@media (max-height: 760px) {
  .share-instructions-page {
    padding-top: 1rem;
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 0.5rem);
  }

  .share-instructions-logo {
    width: 80px;
  }

  .share-instructions-title {
    font-size: 1.8rem;
  }

  .share-instructions-description {
    max-width: 17rem;
    font-size: 0.95rem;
    line-height: 1.45;
  }

  .share-instructions-preview {
    width: min(48vw, 9.5rem);
  }

  .share-instructions-steps,
  .share-instructions-bartender {
    max-width: 19rem;
    line-height: 1.45;
  }

  .share-instructions-actions {
    gap: 0.625rem;
  }

  .share-instructions-button {
    min-height: 2.8rem;
    font-size: 0.82rem;
    letter-spacing: 0.12em;
  }
}

@media (max-height: 680px) {
  .share-instructions-page {
    padding-top: 0.75rem;
    padding-left: 1rem;
    padding-right: 1rem;
    row-gap: 0.5rem;
  }

  .share-instructions-logo {
    width: 70px;
    margin-bottom: 0.75rem;
  }

  .share-instructions-title {
    margin-bottom: 0.45rem;
    font-size: 1.55rem;
  }

  .share-instructions-description {
    max-width: 16rem;
    font-size: 0.88rem;
    line-height: 1.35;
  }

  .share-instructions-preview {
    width: min(45vw, 8rem);
    border-radius: 1.1rem;
  }

  .share-instructions-steps,
  .share-instructions-bartender {
    margin-top: 0.6rem;
    max-width: 17rem;
    font-size: 0.84rem;
    line-height: 1.35;
  }

  .share-instructions-actions {
    gap: 0.5rem;
  }

  .share-instructions-button {
    min-height: 2.6rem;
    font-size: 0.78rem;
  }
}
</style>
